import { useEffect, useState } from "react";
import { useAuth } from "react-oidc-context";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilters } from "@fortawesome/pro-duotone-svg-icons";
import { Badge, CheckBox, ModalPopup } from "../../common";
import FilterEditIcon from "./FilterEditIcon";
import AdvancedFilterModal from "./AdvancedFilterModal";
import CheckboxTreeNodeDto from "../../../types/dtos/generic/CheckboxTreeNodeDto";
import FilterCheckboxTreeApiResponseDto from "../../../types/analytics/FilterCheckboxTreeApiResponseDto";
import locationApi from "../../../api/dashboard/locationApi";
import jobTitleApi from "../../../api/dashboard/jobTitleApi";
import appraisalLevelApi from "../../../api/dashboard/appraisalLevelApi";
import AnalyticsFilterInputs from "../../../types/analytics/AnalyticsFilterInputs";
import userGroupApi from "../../../api/admin/userGroupApi";
import { BasicUserGroupDto } from "../../../types/admin/user-groups/BasicUserGroupDto";
import {
  checklistCheckedFromAppliedIds,
  flattenCheckboxTreeToValues,
  submitIdsFromChecklistSelection,
} from "../../../helpers/searchableFilterChecklist";
import { audienceFacetEffectiveCount } from "../../../helpers/analytics/audienceFacetHelpers";

export type AnalyticsAdvancedFilterType =
  | "USER-GROUP"
  | "JOB-TITLE"
  | "APPRAISAL-LEVEL"
  | "LOCATION";

interface AdvancedFiltersProps {
  inEditMode: boolean;
  selectedValues: AnalyticsFilterInputs;
  onBeginEditMode(): void;
  onLocationPickerChange: (selectedLocationIds: number[]) => void;
  onAppraisalLevelPickerChange: (selectedAppraisalLevelIds: number[]) => void;
  onJobTitlePickerChange: (selectedJobTitleIds: number[]) => void;
  onChangeApprovedDataOnly: (newValue: boolean) => void;
  onChangeIncludeLeavers: (newValue: boolean) => void;
  onUserGroupPickerChange: (selectedUserGroupIds: number[]) => void;
  /** Hide the Include leavers checkbox when a sibling employee-status control is shown. */
  hideEmployeeStatus?: boolean;
  /**
   * When provided, facet rows call this instead of opening ModalPopup
   * (use for in-panel drill-in so a modal does not stack over a drawer).
   */
  onFacetOpen?: (filterType: AnalyticsAdvancedFilterType) => void;
}

function AdvancedFilters({
  inEditMode,
  selectedValues,
  onBeginEditMode,
  onLocationPickerChange,
  onAppraisalLevelPickerChange,
  onJobTitlePickerChange,
  onChangeApprovedDataOnly,
  onChangeIncludeLeavers,
  onUserGroupPickerChange,
  hideEmployeeStatus = false,
  onFacetOpen,
}: AdvancedFiltersProps) {
  const auth = useAuth();
  const userGrpApi = new userGroupApi(auth.user?.access_token);

  const appLevelApi = new appraisalLevelApi(auth.user?.access_token);
  const locApi = new locationApi(auth.user?.access_token);
  const jTitleApi = new jobTitleApi(auth.user?.access_token);

  // State
  const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);
  const [modalTitle, setModalTitle] = useState<string>("");
  const [filterType, setFilterType] =
    useState<AnalyticsAdvancedFilterType | null>(null);
  const [approvedOnlyTicked, setApprovedOnlyTicked] = useState<boolean>(
    selectedValues.includePartiallyApproved === false
  );
  const [includeLeaversTicked, setIncludeLeaversTicked] = useState<boolean>(
    selectedValues.activeUsersOnly === false
  );

  const [locations, setLocations] = useState<CheckboxTreeNodeDto[]>();
  const [isLocationsLoading, setIsLocationsLoading] = useState<boolean>(false);
  const [locationTreeChecked, setLocationTreeChecked] = useState<string[]>([]);
  const [locationTreeExpanded, setLocationTreeExpanded] = useState<string[]>(
    []
  );
  const [selectedLocationCount, setSelectedLocationCount] = useState<number>(
    selectedValues.locationIds?.length ?? 0
  );

  const [jobTitles, setJobTitles] = useState<CheckboxTreeNodeDto[]>();
  const [isJobTitlesLoading, setIsJobTitlesLoading] = useState<boolean>(false);
  const [jobTitleTreeChecked, setJobTitleTreeChecked] = useState<string[]>([]);
  const [jobTitleTreeExpanded, setJobTitleTreeExpanded] = useState<string[]>(
    []
  );
  const [selectedJobTitleCount, setSelectedJobTitleCount] = useState<number>(
    selectedValues.jobTitleIds?.length ?? 0
  );

  const [appraisalLevels, setAppraisalLevels] =
    useState<CheckboxTreeNodeDto[]>();
  const [isAppraisalLevelsLoading, setIsAppraisalLevelsLoading] =
    useState<boolean>(false);
  const [appraisalLevelTreeChecked, setAppraisalLevelTreeChecked] = useState<
    string[]
  >([]);
  const [appraisalLevelTreeExpanded, setAppraisalLevelTreeExpanded] = useState<
    string[]
  >([]);
  const [selectedAppraisalLevelCount, setSelectedAppraisalLevelCount] =
    useState<number>(selectedValues.appraisalLevelIds?.length ?? 0);

  const [userGroups, setUserGroups] = useState<BasicUserGroupDto[]>();
  const [isUserGroupsLoading, setIsUserGroupsLoading] =
    useState<boolean>(false);
  const [selectedUserGroupCount, setSelectedUserGroupCount] = useState<number>(
    selectedValues.userGroupIds?.length ?? 0
  );

  const [userGroupTreeChecked, setUserGroupTreeChecked] = useState<string[]>(
    []
  );
  const [userGroupTreeExpanded, setUserGroupTreeExpanded] = useState<string[]>(
    []
  );
  // Hooks
  // Need to load User Groups to determine whether to show the filter or not
  useEffect(() => {
    loadUserGroups();
  }, []);

  // Load location tree when there is a location filter so the badge can show
  // compressed root counts (parent + descendants → 1) without opening the editor.
  useEffect(() => {
    if ((selectedValues.locationIds?.length ?? 0) === 0) return;
    if (locations !== undefined || isLocationsLoading) return;
    setIsLocationsLoading(true);
    locApi.getLocationCheckboxTreeItemsByClientId(
      false,
      (data: FilterCheckboxTreeApiResponseDto) => {
        setLocations(data.nodes);
        setIsLocationsLoading(false);
      },
      (error) => {
        console.error(error);
        setIsLocationsLoading(false);
      }
    );
    // Do not depend on isLocationsLoading — an error sets it false and would
    // immediately re-fire this effect into an endless retry loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load once when ids appear
  }, [selectedValues.locationIds, locations]);

  useEffect(() => {
    // As the applied filters change in the parent state, ensure the modals launch with a matching state,
    // e.g. so the ticked appraisal levels remain ticked when the modal is re-opened
    const appraisalLevelIds = selectedValues.appraisalLevelIds ?? [];
    setSelectedAppraisalLevelCount(appraisalLevelIds.length);
    const appraisalAll = flattenCheckboxTreeToValues(appraisalLevels);
    setAppraisalLevelTreeChecked(
      checklistCheckedFromAppliedIds(appraisalLevelIds, appraisalAll)
    );

    setSelectedLocationCount(
      audienceFacetEffectiveCount(
        "LOCATION",
        selectedValues.locationIds,
        locations
      )
    );
    const locationAll = flattenCheckboxTreeToValues(locations);
    setLocationTreeChecked(
      checklistCheckedFromAppliedIds(selectedValues.locationIds, locationAll)
    );

    const jobTitleIds = selectedValues.jobTitleIds ?? [];
    setSelectedJobTitleCount(jobTitleIds.length);
    const jobTitleAll = flattenCheckboxTreeToValues(jobTitles);
    setJobTitleTreeChecked(
      checklistCheckedFromAppliedIds(jobTitleIds, jobTitleAll)
    );

    const userGroupIds = selectedValues.userGroupIds ?? [];
    const userGroupAll =
      userGroups?.map((g) => g.id.toString()) ?? [];
    setUserGroupTreeChecked(
      checklistCheckedFromAppliedIds(userGroupIds, userGroupAll)
    );
  }, [
    selectedValues.userGroupIds,
    selectedValues.jobTitleIds,
    selectedValues.appraisalLevelIds,
    selectedValues.locationIds,
    jobTitles,
    appraisalLevels,
    userGroups,
    locations,
  ]);

  // When opening a specific filter modal, sync that filter's state from selectedValues
  // so reopened modals always show the current applied selections (e.g. after Apply filter).
  // Include selectedValues.* in deps so we always read the latest when the effect runs.
  useEffect(() => {
    if (!modalIsOpen || filterType === null) return;
    switch (filterType) {
      case "APPRAISAL-LEVEL": {
        const ids = selectedValues.appraisalLevelIds ?? [];
        const all = flattenCheckboxTreeToValues(appraisalLevels);
        setAppraisalLevelTreeChecked(
          checklistCheckedFromAppliedIds(ids, all)
        );
        setSelectedAppraisalLevelCount(ids.length);
        break;
      }
      case "JOB-TITLE": {
        const ids = selectedValues.jobTitleIds ?? [];
        const all = flattenCheckboxTreeToValues(jobTitles);
        setJobTitleTreeChecked(checklistCheckedFromAppliedIds(ids, all));
        setSelectedJobTitleCount(ids.length);
        break;
      }
      case "USER-GROUP": {
        const ids = selectedValues.userGroupIds ?? [];
        const all = userGroups?.map((g) => g.id.toString()) ?? [];
        setUserGroupTreeChecked(checklistCheckedFromAppliedIds(ids, all));
        setSelectedUserGroupCount(ids.length);
        break;
      }
      case "LOCATION": {
        const ids = selectedValues.locationIds ?? [];
        const all = flattenCheckboxTreeToValues(locations);
        setLocationTreeChecked(checklistCheckedFromAppliedIds(ids, all));
        setSelectedLocationCount(ids.length);
        break;
      }
      default:
        break;
    }
  }, [
    modalIsOpen,
    filterType,
    selectedValues.appraisalLevelIds,
    selectedValues.jobTitleIds,
    selectedValues.userGroupIds,
    selectedValues.locationIds,
    jobTitles,
    appraisalLevels,
    userGroups,
    locations,
  ]);

  // Compute advanced filter items based on loaded user groups
  const advancedFilterItems: Array<{
    type: AnalyticsAdvancedFilterType;
    text: string;
  }> = [
    { type: "JOB-TITLE", text: "Job Titles" },
    { type: "APPRAISAL-LEVEL", text: "Levels" },
    { type: "LOCATION", text: "Locations" },
  ];

  // Only show user groups filter if we have loaded groups and there are some
  if (userGroups && userGroups.length > 0) {
    advancedFilterItems.unshift({ type: "USER-GROUP", text: "User Groups" });
  }

  const loadUserGroups = () => {
    setIsUserGroupsLoading(true);
    userGrpApi.getAnalyticsUserGroups(
      (data) => {
        setUserGroups(data);
        setIsUserGroupsLoading(false);
      },
      (error) => {
        console.error(error);
        setIsUserGroupsLoading(false);
      }
    );
  };

  /** When the user clicks the primary button from inside an advanced filter modal */
  const onAddFiltersClick = () => {
    setModalIsOpen(false);
    switch (filterType) {
      case "USER-GROUP": {
        const all = userGroups?.map((g) => g.id.toString()) ?? [];
        const submit = submitIdsFromChecklistSelection(
          userGroupTreeChecked,
          all
        );
        setSelectedUserGroupCount(submit.length);
        onUserGroupPickerChange(submit);
        break;
      }
      case "JOB-TITLE": {
        const all = flattenCheckboxTreeToValues(jobTitles);
        const submit = submitIdsFromChecklistSelection(
          jobTitleTreeChecked,
          all
        );
        setSelectedJobTitleCount(submit.length);
        onJobTitlePickerChange(submit);
        break;
      }
      case "APPRAISAL-LEVEL": {
        const all = flattenCheckboxTreeToValues(appraisalLevels);
        const submit = submitIdsFromChecklistSelection(
          appraisalLevelTreeChecked,
          all
        );
        setSelectedAppraisalLevelCount(submit.length);
        onAppraisalLevelPickerChange(submit);
        break;
      }
      case "LOCATION": {
        const all = flattenCheckboxTreeToValues(locations);
        const submit = submitIdsFromChecklistSelection(
          locationTreeChecked,
          all
        );
        setSelectedLocationCount(
          audienceFacetEffectiveCount("LOCATION", submit, locations)
        );
        onLocationPickerChange(submit);
        break;
      }
      default:
        break;
    }
  };

  const onLocationFilterClick = (): void => {
    setFilterType("LOCATION");
    if (locations === undefined) {
      setIsLocationsLoading(true);

      // Call the API to load the necessary state
      const successCallback = (data: FilterCheckboxTreeApiResponseDto) => {
        setLocations(data.nodes);
        setIsLocationsLoading(false);
      };

      const errorCallback = (error: any) => {
        console.error(error);
      };

      locApi.getLocationCheckboxTreeItemsByClientId(
        false,
        successCallback,
        errorCallback
      );
    }
  };

  const onJobTitleFilterClick = (): void => {
    setFilterType("JOB-TITLE");
    if (jobTitles === undefined) {
      setIsJobTitlesLoading(true);

      // Call the API to load the necessary state
      const successCallback = (data: FilterCheckboxTreeApiResponseDto) => {
        setJobTitles(data.nodes);
        setIsJobTitlesLoading(false);
      };

      const errorCallback = (error: any) => {
        console.error(error);
      };

      jTitleApi.getJobTitleCheckboxTreeItemsByClientId(
        successCallback,
        errorCallback
      );
    }
  };

  const onAppraisalFilterClick = (): void => {
    setFilterType("APPRAISAL-LEVEL");
    if (appraisalLevels === undefined) {
      setIsAppraisalLevelsLoading(true);

      // Call the API to load the necessary state
      const successCallback = (data: FilterCheckboxTreeApiResponseDto) => {
        setAppraisalLevels(data.nodes);
        setIsAppraisalLevelsLoading(false);
      };

      const errorCallback = (error: any) => {
        console.error(error);
      };

      appLevelApi.getAppraisalLevelCheckboxTreeItemsByClientId(
        successCallback,
        errorCallback
      );
    }
  };

  const onUserGroupFilterClick = (): void => {
    setFilterType("USER-GROUP");
  };

  const clearSelections = (filterType: AnalyticsAdvancedFilterType) => {
    if (filterType === "USER-GROUP") {
      setUserGroupTreeChecked(
        userGroups?.map((g) => g.id.toString()) ?? []
      );
      setUserGroupTreeExpanded([]);
      setSelectedUserGroupCount(0);
    } else if (filterType === "JOB-TITLE") {
      setJobTitleTreeChecked(flattenCheckboxTreeToValues(jobTitles));
      setJobTitleTreeExpanded([]);
      setSelectedJobTitleCount(0);
    } else if (filterType === "APPRAISAL-LEVEL") {
      setAppraisalLevelTreeChecked(
        flattenCheckboxTreeToValues(appraisalLevels)
      );
      setAppraisalLevelTreeExpanded([]);
      setSelectedAppraisalLevelCount(0);
    } else if (filterType === "LOCATION") {
      setLocationTreeChecked(flattenCheckboxTreeToValues(locations));
      setLocationTreeExpanded([]);
      setSelectedLocationCount(0);
    }
  };

  const onOpenFilterModal = (nextFilterType: AnalyticsAdvancedFilterType) => {
    if (onFacetOpen) {
      onFacetOpen(nextFilterType);
      return;
    }
    switch (nextFilterType) {
      case "USER-GROUP":
        setModalTitle("User Groups");
        onUserGroupFilterClick();
        break;
      case "JOB-TITLE":
        setModalTitle("Job Titles");
        onJobTitleFilterClick();
        break;
      case "APPRAISAL-LEVEL":
        setModalTitle("Levels");
        onAppraisalFilterClick();
        break;
      case "LOCATION":
        setModalTitle("Locations");
        onLocationFilterClick();
        break;
      default:
        setFilterType(null);
    }
    setModalIsOpen(true);
  };

  const onCheckChangedApprovedDataOnly = (isChecked: boolean) => {
    setApprovedOnlyTicked(isChecked);
    onChangeApprovedDataOnly(isChecked);
  };

  const onCheckChangedIncludeLeavers = (isChecked: boolean) => {
    setIncludeLeaversTicked(isChecked);
    onChangeIncludeLeavers(isChecked);
  };

  if (!inEditMode) {
    // Get the count of applied filters via the state held for the actual applied values
    let filterCount = 0;
    if (selectedValues.userGroupIds?.length ?? 0 > 0) {
      filterCount++;
    }
    if (selectedValues.locationIds?.length ?? 0 > 0) {
      filterCount++;
    }
    if (selectedValues.jobTitleIds?.length ?? 0 > 0) {
      filterCount++;
    }
    if (selectedValues.appraisalLevelIds?.length ?? 0 > 0) {
      filterCount++;
    }
    return (
      <button className="group" onClick={onBeginEditMode}>
        <FontAwesomeIcon icon={faFilters} />
        <Badge
          text={filterCount.toString()}
          backgroundColourClassName="accent-bg"
          textColourClassName="text-white"
        />
        <span>Advanced {filterCount === 1 ? "Filter" : "Filters"}</span>
        <FilterEditIcon extraClassNames="ml-2" />
      </button>
    );
  }

  return (
    <>
      <div>
        {advancedFilterItems.map((filterItem) => {
          const selectedItemCount =
            filterItem.type === "JOB-TITLE"
              ? selectedJobTitleCount
              : filterItem.type === "APPRAISAL-LEVEL"
              ? selectedAppraisalLevelCount
              : filterItem.type === "LOCATION"
              ? selectedLocationCount
              : filterItem.type === "USER-GROUP"
              ? selectedUserGroupCount
              : 0;
          const selectedItemBadgeText =
            selectedItemCount >= 100 ? "99+" : selectedItemCount.toString();
          return (
            <div
              key={filterItem.type}
              className="text-sm mb-2 bg-gray-200 py-2 px-6 cursor-pointer group rounded-sm hover:bg-gray-300"
              onClick={() => onOpenFilterModal(filterItem.type)}
            >
              <div className="flex flex-row">
                <div className="grow">
                  <span>{filterItem.text}</span>
                  {selectedItemCount > 0 && (
                    <Badge
                      text={selectedItemBadgeText}
                      backgroundColourClassName="accent-bg"
                      textColourClassName="text-white"
                    />
                  )}
                </div>
                <div className="grow-0 text-right w-2">
                  <FilterEditIcon />
                </div>
              </div>
            </div>
          );
        })}
        <div className="px-2 mb-2">
          <hr className="border-gray-300" />
        </div>
        <div className="mb-2 pl-2 pr-4">
          <CheckBox
            labelText="Approved data only"
            onChange={onCheckChangedApprovedDataOnly}
            selected={approvedOnlyTicked}
            labelClassNames="text-sm pt-1"
            checkboxClassNames="bg-white border border-gray-300"
            controlId="adv-filters-approved-only"
          />
        </div>
        {!hideEmployeeStatus && (
          <div className="mb-2 pl-2 pr-4">
            <CheckBox
              labelText="Include leavers"
              onChange={onCheckChangedIncludeLeavers}
              selected={includeLeaversTicked}
              labelClassNames="text-sm pt-1"
              checkboxClassNames="bg-white border border-gray-300"
              controlId="adv-filters-include-leavers"
            />
          </div>
        )}
      </div>
      {!onFacetOpen && filterType !== null && (
        <ModalPopup
          isOpen={modalIsOpen}
          onOpenChange={setModalIsOpen}
          onPrimaryButtonClick={onAddFiltersClick}
          onSecondaryButtonClick={() => setModalIsOpen(false)}
          title={modalTitle}
          showCloseIcon={true}
          width="SMALL"
          preventInitialAutoFocus
          primaryButtonText="Apply filter"
          secondaryButtonText="Cancel"
        >
          <AdvancedFilterModal
            filterType={filterType}
            onClearSelections={() => clearSelections(filterType)}
            locations={locations}
            isLocationsLoading={isLocationsLoading}
            locationTreeChecked={locationTreeChecked}
            locationTreeExpanded={locationTreeExpanded}
            onLocationTreeChecked={setLocationTreeChecked}
            onLocationTreeExpanded={setLocationTreeExpanded}
            appraisalLevels={appraisalLevels}
            isAppraisalLevelsLoading={isAppraisalLevelsLoading}
            appraisalLevelTreeChecked={appraisalLevelTreeChecked}
            appraisalLevelTreeExpanded={appraisalLevelTreeExpanded}
            onAppraisalLevelTreeChecked={setAppraisalLevelTreeChecked}
            onAppraisalLevelTreeExpanded={setAppraisalLevelTreeExpanded}
            jobTitles={jobTitles}
            isJobTitlesLoading={isJobTitlesLoading}
            jobTitleTreeChecked={jobTitleTreeChecked}
            jobTitleTreeExpanded={jobTitleTreeExpanded}
            onJobTitleTreeChecked={setJobTitleTreeChecked}
            onJobTitleTreeExpanded={setJobTitleTreeExpanded}
            userGroups={userGroups}
            isUserGroupsLoading={isUserGroupsLoading}
            userGroupTreeChecked={userGroupTreeChecked}
            userGroupTreeExpanded={userGroupTreeExpanded}
            onUserGroupTreeChecked={setUserGroupTreeChecked}
            onUserGroupTreeExpanded={setUserGroupTreeExpanded}
          />
        </ModalPopup>
      )}
    </>
  );
}

export default AdvancedFilters;
